import React, { useState, useCallback, useRef } from 'react';
import {
  Heart,
  Lightbulb,
  MoreHorizontal,
  MapPin,
  Loader2,
  Check,
  Trash2,
  Brain,
  Users,
  Sparkle,
  Wallet,
  TrendingUp,
  TrendingDown,
  Link2,
  Expand,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import type { JournalEntry, Language, DimensionImpacts, SemanticLink } from '../types';
import LogoutModal from './LogoutModal';
import AdviceStoryModal from './AdviceStoryModal';

interface MomentAdvice {
  oracleInsight: string;
  shortTermAction: string;
  shortTermTimeframe: string;
  shortTermCompleted?: boolean;
  mediumTermGoal: string;
  mediumTermTimeframe: string;
  mediumTermCompleted?: boolean;
  longTermVision: string;
  longTermTimeframe: string;
  longTermCompleted?: boolean;
  emotionGradient: string;
  inspirationalQuote: string;
  connectedValues: string[];
  connectedGoals: string[];
}

interface InstagramMomentCardProps {
  entry: JournalEntry;
  advice?: MomentAdvice | null;
  language: Language;
  onLike?: (id: string, isLiked: boolean) => void;
  onDelete?: (id: string) => void;
  onGenerateAdvice?: (entry: JournalEntry) => void;
  onCompleteAction?: (entryId: string, actionType: 'short' | 'medium' | 'long', completed: boolean) => void;
  isGeneratingAdvice?: boolean;
  connections?: SemanticLink[];
  allEntries?: JournalEntry[];
}

// Dimension configuration for Instagram-style display
const DIMENSION_CONFIG = {
  salute: { icon: Heart, label: { Italian: 'Salute', English: 'Health', Spanish: 'Salud', German: 'Gesundheit', French: 'Santé' }, color: 'rose' },
  focus: { icon: Brain, label: { Italian: 'Focus', English: 'Focus', Spanish: 'Enfoque', German: 'Fokus', French: 'Focus' }, color: 'amber' },
  connessione: { icon: Users, label: { Italian: 'Connessione', English: 'Connection', Spanish: 'Conexión', German: 'Verbindung', French: 'Connexion' }, color: 'blue' },
  spirito: { icon: Sparkle, label: { Italian: 'Spirito', English: 'Spirit', Spanish: 'Espíritu', German: 'Geist', French: 'Esprit' }, color: 'purple' },
  finanza: { icon: Wallet, label: { Italian: 'Finanza', English: 'Finance', Spanish: 'Finanzas', German: 'Finanzen', French: 'Finance' }, color: 'emerald' },
};

const TRANSLATIONS = {
  Italian: {
    likes: 'Mi piace',
    viewInsight: 'Visualizza insight AI',
    hideInsight: 'Nascondi insight',
    viewConnections: 'Visualizza tutti i',
    connections: 'momenti correlati',
    generateAdvice: 'Genera guida personale',
    generating: 'Generando la tua guida...',
    yourGuide: 'La Tua Guida',
    today: 'Oggi',
    thisWeek: 'Questa Settimana',
    vision: 'Visione',
    viewPlan: 'Vedi piano completo',
    hidePlan: 'Nascondi piano',
    delete: 'Elimina',
    deleteConfirm: 'Sei sicuro di voler eliminare questo momento?',
    deleteModalTitle: 'Elimina momento',
    deleteModalSubtitle: 'Azione permanente',
    deleteModalDescription: 'Questo momento verrà eliminato definitivamente. Non potrai recuperarlo in seguito. Sei sicuro di voler continuare?',
    deleteModalConfirm: 'Elimina',
    deleteModalCancel: 'Annulla',
    ago: 'fa',
    justNow: 'Ora',
    impact: 'Impatto',
    connectedMoments: 'momenti collegati',
  },
  English: {
    likes: 'likes',
    viewInsight: 'View AI insight',
    hideInsight: 'Hide insight',
    viewConnections: 'View all',
    connections: 'related moments',
    generateAdvice: 'Generate personal guide',
    generating: 'Generating your guide...',
    yourGuide: 'Your Guide',
    today: 'Today',
    thisWeek: 'This Week',
    vision: 'Vision',
    viewPlan: 'View full plan',
    hidePlan: 'Hide plan',
    delete: 'Delete',
    deleteConfirm: 'Are you sure you want to delete this moment?',
    deleteModalTitle: 'Delete moment',
    deleteModalSubtitle: 'Permanent action',
    deleteModalDescription: 'This moment will be permanently deleted. You won\'t be able to recover it later. Are you sure you want to continue?',
    deleteModalConfirm: 'Delete',
    deleteModalCancel: 'Cancel',
    ago: 'ago',
    justNow: 'Just now',
    impact: 'Impact',
    connectedMoments: 'connected moments',
  },
  Spanish: {
    likes: 'Me gusta',
    viewInsight: 'Ver insight AI',
    hideInsight: 'Ocultar insight',
    viewConnections: 'Ver todos los',
    connections: 'momentos relacionados',
    generateAdvice: 'Generar guía personal',
    generating: 'Generando tu guía...',
    yourGuide: 'Tu Guía',
    today: 'Hoy',
    thisWeek: 'Esta Semana',
    vision: 'Visión',
    viewPlan: 'Ver plan completo',
    hidePlan: 'Ocultar plan',
    delete: 'Eliminar',
    deleteConfirm: '¿Estás seguro de que quieres eliminar este momento?',
    deleteModalTitle: 'Eliminar momento',
    deleteModalSubtitle: 'Acción permanente',
    deleteModalDescription: 'Este momento se eliminará definitivamente. No podrás recuperarlo después. ¿Estás seguro de que quieres continuar?',
    deleteModalConfirm: 'Eliminar',
    deleteModalCancel: 'Cancelar',
    ago: 'hace',
    justNow: 'Ahora',
    impact: 'Impacto',
    connectedMoments: 'momentos conectados',
  },
  German: {
    likes: 'Gefällt mir',
    viewInsight: 'AI Einsicht anzeigen',
    hideInsight: 'Einsicht verbergen',
    viewConnections: 'Alle',
    connections: 'verknüpften Momente anzeigen',
    generateAdvice: 'Persönlichen Guide generieren',
    generating: 'Generiere deinen Guide...',
    yourGuide: 'Dein Guide',
    today: 'Heute',
    thisWeek: 'Diese Woche',
    vision: 'Vision',
    viewPlan: 'Vollständigen Plan anzeigen',
    hidePlan: 'Plan verbergen',
    delete: 'Löschen',
    deleteConfirm: 'Bist du sicher, dass du diesen Moment löschen möchtest?',
    deleteModalTitle: 'Moment löschen',
    deleteModalSubtitle: 'Permanente Aktion',
    deleteModalDescription: 'Dieser Moment wird dauerhaft gelöscht. Du kannst ihn später nicht wiederherstellen. Bist du sicher, dass du fortfahren möchtest?',
    deleteModalConfirm: 'Löschen',
    deleteModalCancel: 'Abbrechen',
    ago: 'vor',
    justNow: 'Gerade',
    impact: 'Einfluss',
    connectedMoments: 'verbundene Momente',
  },
  French: {
    likes: 'J\'aime',
    viewInsight: 'Voir insight AI',
    hideInsight: 'Masquer insight',
    viewConnections: 'Voir tous les',
    connections: 'moments liés',
    generateAdvice: 'Générer guide personnel',
    generating: 'Génération de ton guide...',
    yourGuide: 'Ton Guide',
    today: 'Aujourd\'hui',
    thisWeek: 'Cette Semaine',
    vision: 'Vision',
    viewPlan: 'Voir plan complet',
    hidePlan: 'Masquer plan',
    delete: 'Supprimer',
    deleteConfirm: 'Es-tu sûr de vouloir supprimer ce moment?',
    deleteModalTitle: 'Supprimer le moment',
    deleteModalSubtitle: 'Action permanente',
    deleteModalDescription: 'Ce moment sera définitivement supprimé. Tu ne pourras pas le récupérer plus tard. Es-tu sûr de vouloir continuer?',
    deleteModalConfirm: 'Supprimer',
    deleteModalCancel: 'Annuler',
    impact: 'Impact',
    connectedMoments: 'moments connectés',
    ago: 'il y a',
    justNow: 'Maintenant',
  },
};

// Emotion to gradient mapping
const EMOTION_GRADIENTS: Record<string, string> = {
  '😊': 'from-amber-400 to-yellow-500',
  '😄': 'from-amber-400 to-orange-500',
  '🥰': 'from-pink-400 to-rose-500',
  '😌': 'from-teal-400 to-cyan-500',
  '🤔': 'from-indigo-400 to-purple-500',
  '😢': 'from-blue-400 to-indigo-500',
  '😤': 'from-red-400 to-rose-500',
  '💪': 'from-emerald-400 to-teal-500',
  '🎯': 'from-violet-400 to-purple-500',
  '✨': 'from-purple-400 to-pink-500',
  '🙏': 'from-amber-300 to-orange-400',
  '💭': 'from-slate-400 to-gray-500',
};

const getLocaleCode = (language: Language): string => {
  const localeMap: Record<Language, string> = {
    Italian: 'it-IT',
    English: 'en-US',
    Spanish: 'es-ES',
    German: 'de-DE',
    French: 'fr-FR',
  };
  return localeMap[language] || 'en-US';
};

const formatTimeAgo = (timestamp: number, t: any, language: Language): string => {
  const diff = Date.now() - timestamp;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (mins < 1) return t.justNow;
  if (mins < 60) return `${mins}m ${t.ago}`;
  if (hours < 24) return `${hours}h ${t.ago}`;
  if (days < 7) return `${days}d ${t.ago}`;
  return new Date(timestamp).toLocaleDateString(getLocaleCode(language), { day: 'numeric', month: 'short' });
};

const InstagramMomentCard: React.FC<InstagramMomentCardProps> = ({
  entry,
  advice,
  language,
  onLike,
  onDelete,
  onGenerateAdvice,
  onCompleteAction,
  isGeneratingAdvice = false,
  connections = [],
  allEntries = [],
}) => {
  const t = TRANSLATIONS[language] || TRANSLATIONS.English;
  const [showMenu, setShowMenu] = useState(false);
  const [showInsight, setShowInsight] = useState(false);
  const [showHeartAnimation, setShowHeartAnimation] = useState(false);
  const [showConnections, setShowConnections] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showAdviceStory, setShowAdviceStory] = useState(false);
  const [showPlan, setShowPlan] = useState(false);
  const lastTapRef = useRef<number>(0);

  // Always use emotion gradient, don't change it based on advice
  const gradient = EMOTION_GRADIENTS[entry.emotion] || 'from-indigo-400 to-purple-500';
  const hasAdvice = advice && advice.oracleInsight;
  const isLiked = entry.isLiked || false;
  const people = entry.people as Array<{ name: string; relationship?: string }> | undefined;
  const places = entry.places as Array<{ name: string }> | undefined;

  // Double tap handler for like
  const handleDoubleTap = useCallback(() => {
    const now = Date.now();
    if (now - lastTapRef.current < 300) {
      // Double tap detected
      if (!isLiked && onLike) {
        onLike(entry.id, true);
        setShowHeartAnimation(true);
        setTimeout(() => setShowHeartAnimation(false), 1000);
      }
    }
    lastTapRef.current = now;
  }, [isLiked, onLike, entry.id]);

  const handleLikeToggle = useCallback(() => {
    if (onLike) {
      onLike(entry.id, !isLiked);
      if (!isLiked) {
        setShowHeartAnimation(true);
        setTimeout(() => setShowHeartAnimation(false), 1000);
      }
    }
  }, [onLike, entry.id, isLiked]);

  const hasMedia = entry.imageUrl;

  return (
    <article className="bg-white border-b border-black/5">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5">
        <div className="flex items-center gap-3">
          {/* Emotion Avatar */}
          <div className={`w-9 h-9 rounded-full bg-gradient-to-br ${gradient} flex items-center justify-center text-lg shadow-sm`}>
            {entry.emotion || '✨'}
          </div>
          {/* Category + Time */}
          <div className="flex items-center gap-1.5">
            <span className="text-[14px] font-semibold text-black">
              {entry.category || 'Moment'}
            </span>
            <span className="text-black/30">•</span>
            <span className="text-[13px] text-black/50">
              {formatTimeAgo(entry.timestamp, t, language)}
            </span>
          </div>
        </div>
        {/* Menu */}
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-2 -mr-2 text-black/60 hover:text-black transition-colors"
          >
            <MoreHorizontal size={20} />
          </button>
          {showMenu && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
              <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-black/10 py-1 z-50 min-w-[140px] animate-in fade-in slide-in-from-top-2 duration-150">
                {onDelete && (
                  <button
                    onClick={() => {
                      setShowDeleteModal(true);
                      setShowMenu(false);
                    }}
                    className="w-full px-4 py-2.5 text-left text-[14px] text-rose-500 hover:bg-rose-50 flex items-center gap-2.5 transition-colors"
                  >
                    <Trash2 size={16} />
                    {t.delete}
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Media Area */}
      <div
        className="relative bg-black/[0.02] cursor-pointer"
        onClick={handleDoubleTap}
      >
        {hasMedia ? (
          <div className="aspect-square">
            <img
              src={entry.imageUrl}
              alt=""
              className="w-full h-full object-cover"
            />
          </div>
        ) : (
          /* Text-only moment with gradient background - Apple-quality design */
          <div className={`aspect-square bg-gradient-to-br ${gradient} flex items-center justify-center p-6 relative overflow-hidden`}>
            {/* Subtle radial gradient overlay for depth */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-transparent to-white/10" />
            
            {/* Frosted glass text container */}
            <div className="relative z-10 max-w-[90%] bg-white/15 backdrop-blur-md rounded-3xl px-6 py-8 shadow-[0_8px_32px_rgba(0,0,0,0.12)] border border-white/20">
              {/* Opening quote mark */}
              <span className="absolute -top-2 left-4 text-5xl text-white/30 font-serif leading-none select-none">"</span>
              
              <p className="text-white text-lg sm:text-xl font-medium text-center leading-relaxed"
                 style={{ textShadow: '0 2px 8px rgba(0,0,0,0.2), 0 1px 2px rgba(0,0,0,0.15)' }}>
                {entry.content}
              </p>
              
              {/* Closing quote mark */}
              <span className="absolute -bottom-6 right-4 text-5xl text-white/30 font-serif leading-none select-none">"</span>
            </div>
          </div>
        )}

        {/* Double-tap heart animation */}
        {showHeartAnimation && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <Heart
              size={80}
              className="text-white fill-white animate-in zoom-in-50 duration-300"
              style={{
                filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.3))',
                animation: 'heartPop 1s ease-out forwards',
              }}
            />
          </div>
        )}

        {/* Analyzing overlay */}
        {entry.isAnalyzing && (
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center">
            <div className="bg-white/95 rounded-2xl px-6 py-4 flex items-center gap-3 shadow-xl">
              <div className="spinner" />
              <span className="text-[15px] font-semibold text-black/80">Analyzing...</span>
            </div>
          </div>
        )}
      </div>

      {/* Action Bar */}
      <div className="flex items-center justify-between px-3 py-2">
        <div className="flex items-center gap-4">
          {/* Like Button */}
          <button
            onClick={handleLikeToggle}
            className="p-1 transition-transform active:scale-90"
          >
            <Heart
              size={26}
              className={isLiked ? 'text-rose-500 fill-rose-500' : 'text-black/80'}
            />
          </button>

          {/* Guide Button - Moved next to Like */}
          {onGenerateAdvice && !hasAdvice && (
            <button
              onClick={() => onGenerateAdvice(entry)}
              disabled={isGeneratingAdvice}
              className={`p-1 transition-colors ${
                isGeneratingAdvice ? 'text-amber-400' : 'text-black/80 hover:text-amber-500'
              }`}
            >
              <Lightbulb size={24} className={isGeneratingAdvice ? 'animate-pulse' : ''} />
            </button>
          )}
          {hasAdvice && (
            <button
              onClick={() => setShowPlan(!showPlan)}
              className="p-1 text-amber-500"
            >
              <Lightbulb size={24} className="fill-amber-500" />
            </button>
          )}
        </div>

      </div>

      {/* Caption Area */}
      <div className="px-3 pb-3 space-y-1.5">
        {/* Like count - placeholder for future */}
        {/* <p className="text-[14px] font-semibold">{likeCount} {t.likes}</p> */}

        {/* Caption (for media posts) */}
        {hasMedia && (
          <p className="text-[14px]">
            <span className="font-semibold">{entry.category}</span>{' '}
            <span className="text-black/90">{entry.content}</span>
          </p>
        )}

        {/* People & Places */}
        {(people?.length || places?.length) ? (
          <div className="flex flex-wrap items-center gap-1.5">
            {people?.slice(0, 3).map((p, i) => (
              <span key={`p-${i}`} className="text-[13px] text-indigo-600 font-medium">
                @{p.name}
              </span>
            ))}
            {places?.slice(0, 2).map((p, i) => (
              <span key={`l-${i}`} className="inline-flex items-center gap-0.5 text-[13px] text-black/50">
                <MapPin size={12} />
                {p.name}
              </span>
            ))}
          </div>
        ) : null}

        {/* 5 Dimensions Impact - Instagram-style compact display */}
        {entry.dimensionImpacts && Object.keys(entry.dimensionImpacts).some(key => (entry.dimensionImpacts as any)[key] !== 0) && (
          <div className="pt-2 mt-2 border-t border-black/5">
            <div className="flex items-center gap-1.5 flex-wrap">
              {Object.entries(entry.dimensionImpacts).map(([key, val]) => {
                const value = val as number;
                if (value === 0) return null;
                const config = DIMENSION_CONFIG[key as keyof typeof DIMENSION_CONFIG];
                if (!config) return null;
                const Icon = config.icon;
                const isPositive = value > 0;
                const colorClasses = {
                  rose: isPositive ? 'bg-rose-50 text-rose-600 border-rose-100' : 'bg-rose-50/50 text-rose-400 border-rose-100/50',
                  amber: isPositive ? 'bg-amber-50 text-amber-600 border-amber-100' : 'bg-amber-50/50 text-amber-400 border-amber-100/50',
                  blue: isPositive ? 'bg-blue-50 text-blue-600 border-blue-100' : 'bg-blue-50/50 text-blue-400 border-blue-100/50',
                  purple: isPositive ? 'bg-purple-50 text-purple-600 border-purple-100' : 'bg-purple-50/50 text-purple-400 border-purple-100/50',
                  emerald: isPositive ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : 'bg-emerald-50/50 text-emerald-400 border-emerald-100/50',
                };
                return (
                  <div
                    key={key}
                    className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[11px] font-semibold border ${colorClasses[config.color as keyof typeof colorClasses]}`}
                  >
                    <Icon size={12} />
                    <span>{config.label[language] || config.label.English}</span>
                    <span className="inline-flex items-center">
                      {isPositive ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
                      {isPositive ? '+' : ''}{value}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Connections - Expandable display with details */}
        {connections.length > 0 && (
          <div className="pt-2 mt-1">
            <button
              onClick={() => setShowConnections(!showConnections)}
              className="inline-flex items-center gap-1.5 text-[13px] text-black/50 hover:text-black/70 transition-colors"
            >
              <Link2 size={14} />
              <span>{connections.length} {t.connectedMoments}</span>
              {showConnections ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </button>

            {/* Expanded connections list */}
            {showConnections && (
              <div className="mt-2 space-y-2 animate-in slide-in-from-top-2 duration-200">
                {connections.map((link, idx) => {
                  const linkedEntry = allEntries.find(e => e.id === link.targetEntryId);
                  if (!linkedEntry) return null;

                  const linkTypeLabels: Record<string, Record<string, string>> = {
                    similar: { Italian: 'Momento simile', English: 'Similar moment', Spanish: 'Momento similar', German: 'Ähnlicher Moment', French: 'Moment similaire' },
                    cause: { Italian: 'Ha causato questo', English: 'Caused this', Spanish: 'Causó esto', German: 'Hat dies verursacht', French: 'A causé cela' },
                    effect: { Italian: 'Conseguenza di questo', English: 'Result of this', Spanish: 'Consecuencia de esto', German: 'Folge davon', French: 'Conséquence de cela' },
                    contradiction: { Italian: 'In contrasto', English: 'In contrast', Spanish: 'En contraste', German: 'Im Gegensatz', French: 'En contraste' },
                    progression: { Italian: 'Evoluzione', English: 'Evolution', Spanish: 'Evolución', German: 'Entwicklung', French: 'Évolution' },
                    related_decision: { Italian: 'Decisione collegata', English: 'Related decision', Spanish: 'Decisión relacionada', German: 'Verbundene Entscheidung', French: 'Décision liée' },
                    relatedPerson: { Italian: 'Persona collegata', English: 'Connected person', Spanish: 'Persona conectada', German: 'Verbundene Person', French: 'Personne connectée' },
                    patternEvidence: { Italian: 'Pattern ricorrente', English: 'Recurring pattern', Spanish: 'Patrón recurrente', German: 'Wiederkehrendes Muster', French: 'Schéma récurrent' },
                  };

                  return (
                    <div
                      key={idx}
                      className="flex items-start gap-2.5 p-2.5 bg-indigo-50/50 rounded-xl border border-indigo-100/50"
                    >
                      {/* Photo thumbnail or emotion avatar */}
                      {linkedEntry.imageUrl ? (
                        <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0">
                          <img
                            src={linkedEntry.imageUrl}
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-400 to-purple-500 flex items-center justify-center text-sm shrink-0">
                          {linkedEntry.emotion || '✨'}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 mb-0.5">
                          <span className="text-[11px] font-semibold text-indigo-600 uppercase tracking-wide">
                            {linkTypeLabels[link.linkType]?.[language] || link.linkType}
                          </span>
                          <span className="text-[10px] text-black/40">
                            {link.strength}%
                          </span>
                        </div>
                        <p className="text-[13px] text-black/80 leading-snug">
                          {linkedEntry.content}
                        </p>
                        {link.reason && (
                          <p className="text-[11px] text-black/50 mt-1 italic">
                            {link.reason}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* AI Insight Toggle */}
        {entry.aiInsight && !entry.isAnalyzing && (
          <button
            onClick={() => setShowInsight(!showInsight)}
            className="text-[14px] text-black/50 hover:text-black/70 transition-colors"
          >
            {showInsight ? t.hideInsight : t.viewInsight}
          </button>
        )}

        {/* AI Insight Expanded */}
        {showInsight && entry.aiInsight && (
          <div className="mt-2 p-3 bg-indigo-50/80 rounded-xl border border-indigo-100/50 animate-in slide-in-from-top-2 duration-200">
            <p className="text-[14px] text-indigo-900 leading-relaxed">
              {entry.aiInsight}
            </p>
          </div>
        )}

        {/* Generating Advice Indicator */}
        {isGeneratingAdvice && (
          <div className="flex items-center justify-center gap-2 py-2 text-amber-600">
            <div className="spinner spinner-sm" style={{ borderColor: 'rgba(217, 119, 6, 0.2)', borderTopColor: '#d97706' }} />
            <span className="text-[14px] font-medium">{t.generating}</span>
          </div>
        )}

        {/* Oracle Insight (when advice exists) */}
        {hasAdvice && (
          <div className="mt-2 space-y-2">
            <div className="p-3 bg-amber-50/60 rounded-xl border border-amber-100/80">
              <div className="flex items-start gap-2.5">
                <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shadow-sm shrink-0">
                  <Lightbulb size={14} className="text-white" />
                </div>
                <div className="flex-1">
                  <p className="text-[11px] font-bold text-amber-600 uppercase tracking-wide mb-1">
                    {t.yourGuide}
                  </p>
                  <p className="text-[14px] text-black leading-snug">
                    {advice!.oracleInsight}
                  </p>
                </div>
              </div>

              {/* View Plan Button - Opens full-screen story */}
              <button
                onClick={() => setShowAdviceStory(true)}
                className="w-full mt-3 flex items-center justify-center gap-2 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 rounded-xl transition-all active:scale-[0.98] shadow-sm"
              >
                <Expand size={16} className="text-white" />
                <span className="text-[13px] font-bold text-white uppercase tracking-wide">
                  {t.viewPlan}
                </span>
              </button>
            </div>

          </div>
        )}

        {/* Timestamp */}
        <p className="text-[11px] text-black/40 uppercase tracking-wide pt-1">
          {new Date(entry.timestamp).toLocaleDateString(getLocaleCode(language), {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
          })}
        </p>
      </div>

      {/* Heart animation keyframes */}
      <style>{`
        @keyframes heartPop {
          0% { transform: scale(0); opacity: 0; }
          15% { transform: scale(1.2); opacity: 1; }
          30% { transform: scale(0.95); opacity: 1; }
          45% { transform: scale(1); opacity: 1; }
          80% { transform: scale(1); opacity: 1; }
          100% { transform: scale(1); opacity: 0; }
        }
      `}</style>

      {/* Delete Confirmation Modal */}
      <LogoutModal
        isOpen={showDeleteModal}
        onConfirm={() => {
          if (onDelete) {
            onDelete(entry.id);
          }
          setShowDeleteModal(false);
        }}
        onCancel={() => setShowDeleteModal(false)}
        title={t.deleteModalTitle}
        subtitle={t.deleteModalSubtitle}
        description={t.deleteModalDescription}
        confirmText={t.deleteModalConfirm}
        cancelText={t.deleteModalCancel}
      />

      {/* Advice Story Modal - Full screen Instagram-style */}
      {hasAdvice && (
        <AdviceStoryModal
          isOpen={showAdviceStory}
          onClose={() => setShowAdviceStory(false)}
          advice={advice}
          onCompleteAction={(actionType, completed) => {
            onCompleteAction?.(entry.id, actionType, completed);
          }}
          language={language}
          entryContent={entry.content}
        />
      )}
    </article>
  );
};

// Action Item Component
interface ActionItemProps {
  emoji: string;
  label: string;
  action: string;
  completed?: boolean;
  onComplete?: () => void;
  colorClass: string;
  completedColorClass: string;
}

const ActionItem: React.FC<ActionItemProps> = ({
  emoji,
  label,
  action,
  completed,
  onComplete,
  colorClass,
  completedColorClass,
}) => (
  <div className={`flex items-start gap-2.5 p-3 rounded-xl border transition-colors ${
    completed ? completedColorClass : colorClass
  }`}>
    <button
      onClick={onComplete}
      className={`mt-0.5 w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0 transition-all cursor-pointer ${
        completed
          ? 'bg-emerald-500 border-emerald-500 text-white hover:bg-emerald-600'
          : 'border-current opacity-50 hover:opacity-100'
      }`}
    >
      {completed && <Check size={12} />}
    </button>
    <div className="flex-1">
      <div className="flex items-center gap-1.5 mb-1">
        <span className="text-sm">{emoji}</span>
        <p className="text-[11px] font-bold uppercase tracking-wide opacity-70">{label}</p>
      </div>
      <p className={`text-[14px] leading-snug ${completed ? 'line-through opacity-60' : ''}`}>
        {action}
      </p>
    </div>
  </div>
);

export default InstagramMomentCard;
